import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GridViewIcon from '@mui/icons-material/GridView';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import type { User } from '../types';
import { apiRequest } from '../utils/api';
import { clearAuth } from '../utils/auth';
import { formatPhoneNumber, formatPhoneInput, getWhatsAppLink } from '../utils/phoneUtils';

const roles = ['SYSTEM ADMIN', 'SALES REP', 'FIELD AND SHOP TEAM'];

// API helper functions
const fetchUsers = async (): Promise<User[]> => {
  const response = await apiRequest('/users', {
    method: 'GET',
  });

  return response.data as User[];
};

const createUser = async (userData: Omit<User, 'id' | 'createdAt' | 'lastLogin'>, password?: string): Promise<User> => {
  const body: any = {
    username: userData.username,
    email: userData.email,
    phone: userData.phone || null,
    role: userData.role,
    approved: userData.approved,
  };
  
  if (password && password.trim() !== '') {
    body.password = password;
  }
  
  const response = await apiRequest('/users', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  const result = response.data as { user: User };
  return result.user;
};

const updateUser = async (id: number, userData: Partial<Omit<User, 'id' | 'createdAt' | 'lastLogin'>>, password?: string): Promise<User> => {
  const body: any = {
    username: userData.username,
    email: userData.email,
    phone: userData.phone || null,
    role: userData.role,
    approved: userData.approved,
  };
  
  if (password && password.trim() !== '') {
    body.password = password;
  }
  
  const response = await apiRequest(`/users/${id}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  const result = response.data as { user: User };
  return result.user;
};

const deleteUser = async (id: number): Promise<void> => {
  await apiRequest(`/users/${id}`, {
    method: 'DELETE',
  });

  // If axios didn't throw, delete was successful; nothing to return
};

export default function Users() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<Omit<User, 'id'>>({
    username: '',
    email: '',
    phone: '',
    role: 'FIELD AND SHOP TEAM',
    approved: false,
    createdAt: new Date().toLocaleDateString('en-US'),
    lastLogin: 'Never',
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Fetch users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const fetchedUsers = await fetchUsers();
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load users';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      
      // If unauthorized, clear auth and let App.tsx handle redirect
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('token')) {
        clearAuth();
        // Reload page to trigger App.tsx auth check
        window.location.reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = async () => {
    await loadUsers();
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'success' });
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      phone: '',
      role: 'FIELD AND SHOP TEAM',
      approved: false,
      createdAt: new Date().toLocaleDateString('en-US'),
      lastLogin: 'Never',
    });
    setPassword('');
    setShowPassword(false);
    setOpenDialog(true);
  };

  const handleEdit = (id: number) => {
    const user = users.find((u) => u.id === id);
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        role: user.role ?? 'FIELD AND SHOP TEAM',
        approved: user.approved,
        createdAt: user.createdAt ?? '',
        lastLogin: user.lastLogin ?? '',
      });
      setPassword('');
      setShowPassword(false);
      setOpenDialog(true);
    }
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (deleteId !== null) {
      try {
        await deleteUser(deleteId);
      setUsers(users.filter((u) => u.id !== deleteId));
        setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      setOpenDeleteDialog(false);
      setDeleteId(null);
      } catch (error) {
        console.error('Error deleting user:', error);
        setSnackbar({
          open: true,
          message: error instanceof Error ? error.message : 'Failed to delete user',
          severity: 'error',
        });
      }
    }
  };

  const handleSave = async () => {
    try {
    if (!editingUser && (!password || password.trim() === '')) {
      setSnackbar({ open: true, message: 'Password is required for new users', severity: 'error' });
      return;
    }
    if (editingUser) {
      // Update existing user
        const updatedUser = await updateUser(editingUser.id, formData, password);
        setUsers(users.map((u) => (u.id === editingUser.id ? updatedUser : u)));
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
    } else {
      // Create new user
        const newUser = await createUser(formData, password);
        setUsers([...users, newUser]);
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
    }
    setOpenDialog(false);
    setEditingUser(null);
    setPassword('');
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to save user',
        severity: 'error',
      });
    }
  };

  const handleDownload = () => {
    // Simulate download - in real app, this would export CSV/Excel
    const csv = [
      ['ID', 'Username', 'Email', 'Phone', 'Role', 'Approved', 'Created At', 'Last Login'],
      ...users.map((u) => [
        u.id,
        u.username,
        u.email,
        u.phone || '',
        u.role,
        u.approved ? 'Yes' : 'No',
        u.createdAt,
        u.lastLogin,
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'Data exported successfully', severity: 'success' });
  };

  const filteredUsers = users.filter(
    (u) =>
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Users
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Users
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        {showSearch && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setShowSearch(false);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </Box>
        )}
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton title="Grid View">
              <GridViewIcon />
            </IconButton>
            <IconButton title="Filter">
              <FilterListIcon />
            </IconButton>
            <IconButton title="Download" onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
            <IconButton
              title="Search"
              onClick={() => setShowSearch(!showSearch)}
              color={showSearch ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton title="Refresh" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
            >
              Create
            </Button>
          </Box>
        </Toolbar>

        {isMobile ? (
          /* ── Mobile card list ── */
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : displayedUsers.length === 0 ? (
              <Typography align="center" sx={{ py: 4 }} color="text.secondary">
                No users found
              </Typography>
            ) : (
              displayedUsers.map((user, index) => {
                const whatsappLink = getWhatsAppLink(user.phone);
                return (
                  <Card key={user.id} variant="outlined" sx={{ mx: 1, my: 1 }}>
                    <CardContent sx={{ pb: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {index + 1 + page * rowsPerPage}. {user.username}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={user.approved ? 'Approved' : 'Pending'}
                          color={user.approved ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {user.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>Phone:</Typography>
                            <Typography variant="body2">{formatPhoneNumber(user.phone)}</Typography>
                            {whatsappLink && (
                              <Tooltip title="Open WhatsApp">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(whatsappLink, '_blank')}
                                  sx={{ color: '#25D366', p: 0.25 }}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>Role:</Typography>
                          <Typography variant="body2">{user.role}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>Created:</Typography>
                          <Typography variant="body2">{user.createdAt}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>Last login:</Typography>
                          <Typography variant="body2">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-US') : 'Never'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                      <IconButton size="small" onClick={() => handleEdit(user.id)} title="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(user.id)} title="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </Card>
                );
              })
            )}
            <TablePagination
              component="div"
              count={filteredUsers.length}
              rowsPerPageOptions={[5, 10, 25]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : (
          /* ── Desktop table ── */
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : displayedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedUsers.map((user, index) => {
                      const whatsappLink = getWhatsAppLink(user.phone);
                      return (
                        <TableRow key={user.id} hover>
                          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                          <TableCell>{user.username}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {formatPhoneNumber(user.phone)}
                              </Typography>
                              {whatsappLink && (
                                <Tooltip title="Open WhatsApp">
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(whatsappLink, '_blank')}
                                    sx={{
                                      color: '#25D366',
                                      '&:hover': { backgroundColor: 'rgba(37, 211, 102, 0.1)' },
                                    }}
                                  >
                                    <WhatsAppIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{user.role}</TableCell>
                          <TableCell>
                            <Chip
                              label={user.approved ? 'Approved' : 'Pending'}
                              color={user.approved ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{user.createdAt}</TableCell>
                          <TableCell>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-US') : 'Never'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleEdit(user.id)} title="Edit">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(user.id)} title="Delete">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              rowsPerPageOptions={[5, 10, 25]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editingUser ? 'Edit User' : 'Create New User'}
          {isMobile && (
            <IconButton size="small" onClick={() => setOpenDialog(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                const formatted = formatPhoneInput(e.target.value);
                setFormData({ ...formData, phone: formatted });
              }}
              fullWidth
              placeholder="(123) 456-7890"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Role"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              fullWidth
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Approved"
              value={formData.approved ? 'true' : 'false'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  approved: e.target.value === 'true',
                })
              }
              fullWidth
            >
              <MenuItem value="true">Approved</MenuItem>
              <MenuItem value="false">Pending</MenuItem>
            </TextField>
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required={!editingUser}
              helperText={editingUser ? "Change password or leave as is" : "Required — set the user's password"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Created At"
              value={formData.createdAt}
              onChange={(e) =>
                setFormData({ ...formData, createdAt: e.target.value })
              }
              fullWidth
              placeholder="MM/DD/YYYY"
            />
            <TextField
              label="Last Login"
              value={formData.lastLogin}
              onChange={(e) =>
                setFormData({ ...formData, lastLogin: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.username || !formData.email}
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
